import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import Icon from '../components/Icon'
import ToolOptions from '../components/tool/ToolOptions'
import SignPlacer from '../components/tool/SignPlacer'
import PdfEditor from '../components/tool/PdfEditor'
import RegionEditor from '../components/tool/RegionEditor'
import { useToast } from '../components/ToastProvider'
import { TOOLS, ORDER_IMG, ORDER_PDF, ICN, COLORS, GRAD } from '../data/tools'
import { fmtSize, downloadBlob, loadImage, baseName } from '../lib/format'
import { rotatedPreview, rotatedPreviewDeg } from '../lib/imageTools'
import { runTool } from '../lib/process'
import { blobsToZip, pdfFirstPagePreview, renderPdfPage, renderAllPages } from '../lib/pdfTools'
import { Helmet } from 'react-helmet-async'

function defaultOpts(id) {
  switch (TOOLS[id].opts) {
    case 'quality': return { quality: 70 }
    case 'dims': return { lock: true, width: 0, height: 0 }
    case 'crop': return { aspect: { value: undefined, label: 'Freeform' } }
    case 'imgformat': return { format: 'PNG' }
    case 'removebg': return { corner: 'top-left', tolerance: 40 }
    case 'blurface': return { strength: 18, regions: [] }
    case 'rotate': return { transform: 'Rotate right' }
    case 'scale': return { scale: '2' }
    case 'watermark': return { text: '© iLoveImgPdf', position: 'Center' }
    case 'pagesize': return { pagesize: 'A4', orientation: 'Portrait' }
    case 'pages': return { pages: '' }
    case 'level': return { level: 'Recommended' }
    case 'imgquality': return { quality: 'High' }
    case 'editpdf': return { text: 'iLoveImgPdf', page: '1', position: 'Center' }
    case 'sign': return { signature: null, placements: [] }
    case 'password': return { password: '', confirm: '' }
    case 'unlock': return { password: '' }
    case 'wmpdf': return { text: 'CONFIDENTIAL', position: 'Diagonal' }
    case 'rotatepdf': return { angle: '90° right' }
    case 'pagenum': return { format: 'Number', position: 'Bottom center' }
    default: return {}
  }
}

function fmtAccept(t) {
  if (t.accept === 'image/*') return 'JPG, PNG, WebP, GIF'
  if (t.accept === 'application/pdf') return 'PDF'
  if (t.accept.includes('doc')) return 'DOC, DOCX, TXT'
  return t.accept
}

function centeredCrop(w, h, aspect) {
  return centerCrop(makeAspectCrop({ unit: '%', width: 80 }, aspect || w / h, w, h), w, h)
}

function ToolPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const fileInputRef = useRef(null)
  const editorRef = useRef(null)

  const t = TOOLS[id]
  
  const [files, setFiles] = useState([])
  const [opts, setOpts] = useState(defaultOpts(id))
  const [meta, setMeta] = useState(null) // natural {w,h} of first image
  const [drag, setDrag] = useState(false)
  const [stage, setStage] = useState('work') // work | proc | result
  const [progress, setProgress] = useState(0)
  const [result, setResult] = useState(null)
  const [pdfPreview, setPdfPreview] = useState(null) // page-1 preview for PDF results

  // crop state
  const [crop, setCrop] = useState()
  const [src, setSrc] = useState(null)
  const imgRef = useRef(null)

  // rotate live preview
  const [livePreview, setLivePreview] = useState(null)
  const loadedImgRef = useRef(null)

  // sign: all page images to drop the signature onto
  const [signPages, setSignPages] = useState(null)

  // Reset everything when the tool changes.
  useEffect(() => {
    setFiles([])
    setOpts(defaultOpts(id))
    setMeta(null)
    setStage('work')
    setResult(null)
    setPdfPreview(null)
    setCrop(undefined)
    setSrc(null)
    setLivePreview(null)
    setSignPages(null)
    loadedImgRef.current = null
    window.scrollTo(0, 0)
  }, [id])

  const setOpt = (k, v) => setOpts((o) => ({ ...o, [k]: v }))
  const themeStyle = t ? { '--c': COLORS[t.cat].c, '--c-tint': COLORS[t.cat].t, '--c-glow': COLORS[t.cat].g } : {}

  const addFiles = async (list) => {
    const arr = [...list].filter((f) => f && f.size >= 0)
    if (!arr.length) return
    const next = t.multi ? files.concat(arr) : arr.slice(0, 1)
    setFiles(next)

    // Load image meta for sizing-aware tools.
    if (t.cat === 'img' && next[0]?.type.startsWith('image/')) {
      try {
        const img = await loadImage(next[0])
        setMeta({ w: img.naturalWidth, h: img.naturalHeight })
        if (t.opts === 'dims') setOpts((o) => ({ ...o, width: img.naturalWidth, height: img.naturalHeight, lock: true }))
        if (id === 'rotate') {
          loadedImgRef.current = img
          setLivePreview(rotatedPreview(img, opts.transform || 'Rotate right'))
        }
      } catch (e) {
        /* ignore */
      }
    }
    if (id === 'crop') {
      const reader = new FileReader()
      reader.onload = () => setSrc(reader.result)
      reader.readAsDataURL(next[0])
    }
    if (id === 'sign' && next[0]) {
      try {
        setSignPages(await renderAllPages(next[0], 1.4))
      } catch (e) {
        /* preview is best-effort */
      }
    }
    if (id === 'rotatepdf' && next[0]) {
      try {
        const { dataUrl } = await renderPdfPage(next[0], 1.5)
        const im = new Image()
        im.onload = () => {
          loadedImgRef.current = im
          setLivePreview(rotatedPreviewDeg(im, parseInt(opts.angle, 10) || 90))
        }
        im.src = dataUrl
      } catch (e) {
        /* preview is best-effort */
      }
    }
  }

  const removeFile = (i) => {
    const next = files.filter((_, idx) => idx !== i)
    setFiles(next)
    if (!next.length) {
      setSrc(null)
      setMeta(null)
      setLivePreview(null)
      setSignPages(null)
      loadedImgRef.current = null
    }
  }

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget
    setCrop(centeredCrop(width, height, opts.aspect?.value))
  }

  // Update the rotate live preview whenever the transform changes.
  useEffect(() => {
    if (id === 'rotate' && loadedImgRef.current) {
      setLivePreview(rotatedPreview(loadedImgRef.current, opts.transform || 'Rotate right'))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.transform])

  // Update the PDF rotate live preview whenever the angle changes.
  useEffect(() => {
    if (id === 'rotatepdf' && loadedImgRef.current) {
      setLivePreview(rotatedPreviewDeg(loadedImgRef.current, parseInt(opts.angle, 10) || 90))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.angle])

  // Re-center crop when aspect preset changes.
  useEffect(() => {
    if (id === 'crop' && imgRef.current) {
      const { width, height } = imgRef.current
      setCrop(centeredCrop(width, height, opts.aspect?.value))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.aspect])

  const reset = () => {
    setFiles([])
    setMeta(null)
    setStage('work')
    setResult(null)
    setPdfPreview(null)
    setSrc(null)
    setCrop(undefined)
    setLivePreview(null)
    setSignPages(null)
    loadedImgRef.current = null
    setOpts(defaultOpts(id))
  }

  const validate = () => {
    if (!files.length) return 'Please add a file first.'
    if (t.opts === 'password' && !opts.password) return 'Please set a password.'
    if (t.opts === 'password' && opts.password !== opts.confirm) return 'Passwords do not match.'
    if (id === 'sign' && !opts.signature) return 'Please draw a signature first.'
    if (id === 'sign' && !(opts.placements || []).length) return 'Click a page to place your signature.'
    if (id === 'blurface' && !(opts.regions || []).length) return 'Drag a box over the area on the image first.'
    if (id === 'unlock' && !opts.password) return 'Enter the PDF password first.'
    return null
  }

  const run = async () => {
    const err = validate()
    if (err) {
      toast(err)
      return
    }

    // Build the opts passed to the processor (inject crop geometry).
    let payload = { ...opts }
    if (id === 'editpdf') {
      if (!editorRef.current?.hasContent()) {
        toast('Add some text, an image or a drawing first.')
        return
      }
      payload.overlay = await editorRef.current.exportOverlay()
    }
    if (id === 'crop') {
      const image = imgRef.current
      if (!crop?.width || !image) {
        toast('Select an area to crop first.')
        return
      }
      payload.crop = {
        x: crop.unit === '%' ? (crop.x / 100) * image.width : crop.x,
        y: crop.unit === '%' ? (crop.y / 100) * image.height : crop.y,
        width: crop.unit === '%' ? (crop.width / 100) * image.width : crop.width,
        height: crop.unit === '%' ? (crop.height / 100) * image.height : crop.height,
        scaleX: image.naturalWidth / image.width,
        scaleY: image.naturalHeight / image.height,
      }
    }

    setStage('proc')
    setProgress(0)
    let p = 0
    const tick = setInterval(() => {
      p = Math.min(90, p + Math.random() * 16 + 6)
      setProgress(p)
    }, 180)

    try {
      const res = await runTool(id, files, payload)
      // For PDF output, render page 1 so the result shows a real preview.
      let preview = null
      if (res.mime === 'application/pdf') {
        try {
          preview = await pdfFirstPagePreview(res.blob)
        } catch (e) {
          /* preview is best-effort */
        }
      }
      clearInterval(tick)
      setProgress(100)
      if (res.note) toast(res.note)
      setTimeout(() => {
        setResult(res)
        setPdfPreview(preview)
        setStage('result')
      }, 350)
    } catch (e) {
      clearInterval(tick)
      setStage('work')
      toast(e.message || 'Something went wrong.')
    }
  }

  const handleDownload = async () => {
    if (!result) return
    if (result.multiOut && result.pages?.length > 1) {
      const zip = await blobsToZip(result.pages, baseName(files[0].name))
      downloadBlob(zip, `${baseName(files[0].name)}-images.zip`)
      toast('Download started ' + files[0].name)
    } else {
      downloadBlob(result.blob, result.name)
      toast('Download started ' + result.name)
    }
  }

  const resultUrl = useMemo(() => (result?.blob ? URL.createObjectURL(result.blob) : null), [result])
  useEffect(() => () => resultUrl && URL.revokeObjectURL(resultUrl), [resultUrl])

  if (!t) {
    return (
      <main className="view">
        <div className="wrap subpage">
          <h1>Tool not found</h1>
          <button className="btn btn-c" style={{ marginTop: 16 }} onClick={() => navigate('/')}>Back home</button>
        </div>
      </main>
    )
  }

  const related = (t.cat === 'img' ? ORDER_IMG : ORDER_PDF).filter((x) => x !== id).slice(0, 4)
  const isImageResult = result && result.mime?.startsWith('image/')

  return (
    <>
     <Helmet>
        <title>{t?.seotitle}</title>

        <meta
          name="description"
          content={t?.metadis}
        />

        <meta
          name="keywords"
          content={t?.keyword}
        />

        <link
          rel="canonical"
          href={`https://iloveimgpdf.com/tool/${id}`}
        />
      </Helmet>
    <main className="view" style={themeStyle}>
      <div className="wrap subpage">
        <div className="crumbs">
          <a onClick={() => navigate('/')}>Home</a>
          <Icon name="chevron-right" size={15} />
          <a onClick={() => navigate('/#' + (t.cat === 'img' ? 'image' : 'pdf'))}>{t.cat === 'img' ? 'Image tools' : 'PDF tools'}</a>
          <Icon name="chevron-right" size={15} />
          <span className="here">{t.name}</span>
        </div>

        <div className="tool-hero">
          <span className="thico" style={{ background: GRAD[id], color: '#fff' }}><Icon name={ICN[id]} size={26} stroke={2} /></span>
          <div>
            <h1>{t.name}</h1>
            <p>{t.desc}</p>
          </div>
        </div>

        {stage === 'work' && (
          <div className="work">
            <div>
              {files.length === 0 ? (
                <>
                  <div
                    className={'dropzone' + (drag ? ' drag' : '')}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDrag(true) }}
                    onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDrag(false) }}
                    onDrop={(e) => { e.preventDefault(); setDrag(false); if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files) }}
                  >
                    <span className="dz-ico"><Icon name="upload" size={32} /></span>
                    <h3>{t.multi ? 'Drop your files here' : 'Drop your file here'}</h3>
                    <div className="sub">{t.multi ? 'or pick a few from your device add as many as you like' : 'or pick one from your device to get started'}</div>
                    <span className="dz-btn"><Icon name="plus" size={18} style={{ color: '#fff' }} />Choose file</span>
                    <div className="dz-formats">Accepted: {fmtAccept(t)}</div>
                  </div>
                  <input ref={fileInputRef} type="file" accept={t.accept} multiple={!!t.multi} hidden onChange={(e) => { addFiles(e.target.files); e.target.value = '' }} />
                </>
              ) : (
                <div className="panel">
                  {/* Edit PDF interactive editor */}
                  {id === 'editpdf' && (
                    <PdfEditor ref={editorRef} file={files[0]} />
                  )}

                  {/* Blur Face region selector */}
                  {id === 'blurface' && files[0] && (
                    <RegionEditor
                      file={files[0]}
                      regions={opts.regions || []}
                      onChange={(r) => setOpt('regions', r)}
                      hint="Drag boxes over the faces or areas to blur."
                    />
                  )}

                  {/* Rotate / flip live preview (image + PDF) */}
                  {(id === 'rotate' || id === 'rotatepdf') && livePreview && (
                    <div style={{ padding: 16, display: 'flex', justifyContent: 'center', background: '#111827', borderRadius: 'var(--r-lg) var(--r-lg) 0 0' }}>
                      <img src={livePreview} alt="rotate preview" style={{ maxHeight: '52vh', maxWidth: '100%', borderRadius: 8, transition: 'opacity .15s' }} />
                    </div>
                  )}

                  {/* Sign: pick any page and drop the signature */}
                  {id === 'sign' && signPages && (
                    <div style={{ borderRadius: 'var(--r-lg) var(--r-lg) 0 0', overflow: 'hidden' }}>
                      <SignPlacer
                        pages={signPages}
                        signature={opts.signature}
                        placements={opts.placements || []}
                        onChange={(p) => setOpt('placements', p)}
                      />
                    </div>
                  )}

                  {/* Crop interactive stage */}
                  {id === 'crop' && src && (
                    <div style={{ padding: 16, display: 'flex', justifyContent: 'center', background: '#111827', borderRadius: 'var(--r-lg) var(--r-lg) 0 0' }}>
                      <ReactCrop crop={crop} onChange={(c) => setCrop(c)} aspect={opts.aspect?.value}>
                        <img ref={imgRef} src={src} alt="to crop" onLoad={onImageLoad} style={{ maxHeight: '52vh', maxWidth: '100%' }} />
                      </ReactCrop>
                    </div>
                  )}

                  <div className="files">
                    {files.map((f, i) => (
                      <div className="file-row" key={i}>
                        <div className="file-thumb">
                          {f.type.startsWith('image/') ? <img src={URL.createObjectURL(f)} alt="" /> : <Icon name="file-text" size={20} />}
                        </div>
                        <div className="file-meta">
                          <div className="fn">{f.name}</div>
                          <div className="fs">{fmtSize(f.size)}</div>
                        </div>
                        <button className="file-x" onClick={() => removeFile(i)} aria-label="Remove"><Icon name="x" size={16} stroke={1.8} /></button>
                      </div>
                    ))}
                    {t.multi && (
                      <div className="add-more" onClick={() => fileInputRef.current?.click()}>
                        <Icon name="plus" size={16} />Add more files
                      </div>
                    )}
                    <input ref={fileInputRef} type="file" accept={t.accept} multiple={!!t.multi} hidden onChange={(e) => { addFiles(e.target.files); e.target.value = '' }} />
                  </div>

                  {id !== 'editpdf' && (
                    <div className="opts">
                      <ToolOptions id={id} opts={opts} setOpt={setOpt} meta={meta} />
                    </div>
                  )}

                  <div className="action-bar">
                    <button className="btn btn-c" onClick={run}>{t.action}<Icon name="arrow-right" size={18} style={{ color: '#fff' }} /></button>
                    <button className="btn btn-soft" onClick={reset}>Clear</button>
                  </div>
                </div>
              )}
              <div className="secure-note">
                <Icon name="lock" size={15} style={{ color: '#16A34A' }} />
                Everything runs locally your files never leave your device.
              </div>
            </div>

            <aside className="rail">
              <div className="rail-card">
                <h4><Icon name="info" size={17} />How to use</h4>
                <ol className="mini-steps">
                  {t.how.map((s, i) => (
                    <li key={i}><span className="mn" /><span>{s}</span></li>
                  ))}
                </ol>
              </div>
              <div className="rail-card">
                <h4><Icon name="list" size={17} />Related tools</h4>
                <div className="rel-list">
                  {related.map((rid) => {
                    const r = TOOLS[rid]
                    return (
                      <div className={'rel ' + r.cat} key={rid} onClick={() => navigate('/tool/' + rid)}>
                        <span className="ri" style={{ background: GRAD[rid], color: '#fff' }}><Icon name={ICN[rid]} size={18} stroke={2} /></span>
                        <div><div className="rn">{r.name}</div><div className="rc">{r.cat === 'img' ? 'Image tool' : 'PDF tool'}</div></div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </aside>
          </div>
        )}

        {stage === 'proc' && (
          <div className="center-card">
            <div className="proc-ring">
              <svg width="96" height="96" viewBox="0 0 96 96">
                <circle cx="48" cy="48" r="42" fill="none" stroke="var(--line)" strokeWidth="8" />
                <circle cx="48" cy="48" r="42" fill="none" stroke="var(--c)" strokeWidth="8" strokeLinecap="round" strokeDasharray="264" strokeDashoffset={264 - (264 * progress) / 100} style={{ transition: 'stroke-dashoffset .25s ease' }} />
              </svg>
              <span className="pc">{Math.floor(progress)}%</span>
            </div>
            <h2>{t.action}…</h2>
            <div className="psub">Your {files.length > 1 ? files.length + ' files are' : 'file is'} being processed securely.</div>
            <div className="proc-file">
              <span className="pf-ic"><Icon name={files[0]?.type.startsWith('image/') ? 'image' : 'file-text'} size={16} /></span>
              {files.length > 1 ? files.length + ' files' : files[0]?.name}
            </div>
            <div className="barwrap"><div className="barfill" style={{ width: progress + '%' }} /></div>
          </div>
        )}

        {stage === 'result' && result && (
          <div className="center-card">
            <div className="res-badge"><Icon name="check" size={36} /></div>
            <div className="res-title">Your file is ready</div>
            <div className="res-sub">{t.name} complete saved as {result.name.split('.').pop().toUpperCase()}.</div>

            <div className="res-preview">
              {isImageResult ? (
                <img src={resultUrl} alt="result" />
              ) : pdfPreview ? (
                <img src={pdfPreview} alt="result preview" />
              ) : (
                <div className="res-doc"><Icon name="file" size={40} /><div className="rd-name">{result.name}</div></div>
              )}
            </div>

            <div className="res-stats">
              {(() => {
                const before = files.reduce((s, f) => s + f.size, 0)
                const after = result.blob.size
                const saved = before ? Math.round((1 - after / before) * 100) : 0
                if (['compress', 'compresspdf'].includes(id)) {
                  return (
                    <>
                      <div className="res-stat"><div className="rv">{fmtSize(before)}</div><div className="rl">BEFORE</div></div>
                      <div className="res-stat"><div className="rv good">{fmtSize(after)}</div><div className="rl">AFTER</div></div>
                      <div className="res-stat"><div className={'rv' + (saved > 0 ? ' good' : '')}>{saved > 0 ? saved + '%' : '—'}</div><div className="rl">SAVED</div></div>
                    </>
                  )
                }
                if (result.multiOut) {
                  return (
                    <>
                      <div className="res-stat"><div className="rv">{result.pages.length}</div><div className="rl">PAGES</div></div>
                      <div className="res-stat"><div className="rv good">JPG</div><div className="rl">FORMAT</div></div>
                      <div className="res-stat"><div className="rv">{fmtSize(result.pages.reduce((s, b) => s + b.size, 0))}</div><div className="rl">TOTAL</div></div>
                    </>
                  )
                }
                return (
                  <>
                    <div className="res-stat"><div className="rv">{fmtSize(before)}</div><div className="rl">INPUT</div></div>
                    <div className="res-stat"><div className="rv good">Done</div><div className="rl">STATUS</div></div>
                    <div className="res-stat"><div className="rv">{result.name.split('.').pop().toUpperCase()}</div><div className="rl">OUTPUT</div></div>
                  </>
                )
              })()}
            </div>

            <div className="res-actions">
              <button className="btn btn-c" onClick={handleDownload}><Icon name="download" size={18} style={{ color: '#fff' }} />Download</button>
              <button className="btn btn-soft" onClick={reset}>Process another file</button>
            </div>

            <div className="res-more">
              <div className="rm-lbl">You might also need</div>
              <div className="rm-grid">
                {related.slice(0, 3).map((rid) => {
                  const r = TOOLS[rid]
                  return (
                    <div className={'rel ' + r.cat} key={rid} onClick={() => navigate('/tool/' + rid)} style={{ border: '1px solid var(--line)' }}>
                      <span className="ri" style={{ background: GRAD[rid], color: '#fff' }}><Icon name={ICN[rid]} size={18} stroke={2} /></span>
                      <div><div className="rn">{r.name}</div></div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
    </>
  )
}

export default ToolPage
